// ----------------------------------------------------------------------------
// Copyright (c) Ben Coleman, 2020
// Licensed under the MIT License.
//
// Set of methods to call the Graph API, using REST and fetch
// ----------------------------------------------------------------------------

import { store } from "@/vuex/store.js"
import tokenService from './auth/tokenService'
import sharepointApi from '@/apiCallers/promise_handler_db/sharepoint'


const GRAPH_BASE = 'https://graph.microsoft.com/v1.0'
const GRAPH_SCOPES = [ 'sites.readwrite.all','calendars.readwrite','files.readwrite.all', 'group.readwrite.all','sites.fullcontrol.all' ]

let accessToken

// Retry configuration
const MAX_RETRIES = 2
const RETRY_DELAY = 1000

// Custom error for SharePoint/Graph access denied scenarios
class GraphAccessDeniedError extends Error {
  constructor(apiPath) {
    super(`Access denied for: ${apiPath}. The user does not have permission to access this SharePoint site or group. Please contact your administrator.`);
    this.name = 'GraphAccessDeniedError';
    this.apiPath = apiPath;
  }
}

export { GraphAccessDeniedError }

export default {
  //
  // Get details of user, and return as JSON
  // https://docs.microsoft.com/en-us/graph/api/user-get?view=graph-rest-1.0&tabs=http#response-1
  //
  async getSelf() {
    let resp = await callGraph('/me')
    if (resp) {
      let data = await resp.json()
      // console.log(data)
      return data
    }
  },

  //
  // Get user's photo and return as a blob object URL
  // https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL
  //
  async getPhoto(format = 'url') {
    try {
      const resp = await callGraph('/me/photos/240x240/$value')
      if (resp) {
        const blob = await resp.blob()
        if (format === 'dataUrl') {
          return await blobToDataURL(blob)
        }
        return URL.createObjectURL(blob)
      }
      return null
    } catch (e) {
      return null
    }
  },

  // Get another user's photo by Azure AD objectId or UPN (email)
  // Returns a blob URL by default, or base64 data URL when format==='dataUrl'
  async getUserPhoto(userIdOrUpn, { size = '240x240', format = 'url' } = {}) {
    if (!userIdOrUpn) return null
    const sizedPath = `/users/${userIdOrUpn}/photo/$value`
    try {
      let resp = await callGraph(sizedPath)
      if (resp && resp.ok) {
        const blob = await resp.blob()
        if (format === 'dataUrl') {
          return await blobToDataURL(blob)
        }
        return URL.createObjectURL(blob)
      }
      return null
    } catch (e) {
      return null
    }
  },
  async getInboxEmails(accessToken, next_link, filter, folderName) {
    let resp = await callGraph(next_link == null ? '/me/mailFolders/'+ folderName +'/messages/?'+filter+'&$select=sender,subject,createdDateTime,hasAttachments,isRead' : next_link, next_link == null)
    if (resp) {
      let data = await resp.json()
      // console.log("Got data ", data)
      return data
    }

    return data;
  },
  async getMixedEmails(accessToken, next_link, filter) {
    let resp = await callGraph(next_link == null ? '/me/messages/?'+filter+'&$select=sender,subject,createdDateTime,hasAttachments,isRead' : next_link, next_link == null)
    if (resp) {
      let data = await resp.json()
      // console.log("Got data ", data)
      return data
    }

    return data;
  },
  async getUnreadEmailCount() {
    try {
      // Get unread emails from last 30 days only (more relevant, less computational)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const dateFilter = thirtyDaysAgo.toISOString();

      // Use $count and $top=1 to get count without fetching all messages
      const filter = `isRead eq false and receivedDateTime ge ${dateFilter}`;
      let resp = await callGraph(`/me/mailFolders/inbox/messages?$filter=${encodeURIComponent(filter)}&$count=true&$top=1`, true, {
        'ConsistencyLevel': 'eventual'
      })
      if (resp) {
        let data = await resp.json()
        return data['@odata.count'] || 0
      }
      return 0
    } catch (e) {
      console.error('Error fetching unread email count:', e)
      return 0
    }
  },
  async markEmailAsRead(emailId) {
    try {
      accessToken = store.state.aToken;
      const resp = await fetch(`${GRAPH_BASE}/me/messages/${emailId}`, {
        method: 'PATCH',
        headers: {
          authorization: `bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isRead: true })
      });
      return resp.ok;
    } catch (e) {
      console.error('Error marking email as read:', e);
      return false;
    }
  },
  async getSingleFile(groupId, itemId) {
    const resp = await sharepointApi.getSingleDoc(null, groupId, itemId)
    return resp.data
  },
  async getMultipleFiles(links) {
    // Transform Graph API paths ("/groups/{groupId}/drive/items/{itemId}") into {groupId, itemId} objects
    const items = links.map((link) => {
      const parts = link.split('/')
      // Expected format: /groups/{groupId}/drive/items/{itemId}
      const groupIdx = parts.indexOf('groups')
      const itemIdx = parts.indexOf('items')
      return {
        groupId: groupIdx >= 0 ? parts[groupIdx + 1] : null,
        itemId: itemIdx >= 0 ? parts[itemIdx + 1] : null,
      }
    }).filter((item) => item.groupId && item.itemId)
    const resp = await sharepointApi.resolveMultipleLinks(items)
    return resp.data
  },

  async getThumbnails(groupId, itemId) {
    const resp = await sharepointApi.getSingleDoc(null, groupId, itemId)
    return resp.data
  },

  async getFileChildren(groupId, itemId) {
    const resp = await sharepointApi.getItemChildren(null, groupId, itemId)
    return resp.data
  },
  async searchFilesInGroup(groupId, srch_txt) {
    const resp = await sharepointApi.searchFilesInGroup(null, groupId, srch_txt)
    return resp.data
  },

  async getFileByPath(groupId, path) {
    const resp = await sharepointApi.getSingleDoc(null, groupId, path)
    return resp.data && resp.data.id
  },

  async checkFolderContainsFile(groupId, itemId, search_name) {
    const resp = await sharepointApi.checkFolderContainsFile(null, groupId, itemId, search_name)
    return resp.data
  },


  //
  // Search for users
  // https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL
  //
  async searchUsers(searchString, max = 50) {
    let resp = await callGraph(`/users?$filter=startswith(displayName, '${searchString}') or startswith(userPrincipalName, '${searchString}')&$top=${max}`)
    if (resp) {
      let data = await resp.json()
      return data
    }
  },

  //
  // Accessor for access token, only included for demo purposes
  //
  getAccessToken() {
    return accessToken
  },


  async copyFileBetweenGroups(sourceGroupId, fileId, destinationGroupId, destinationFolderId) {
    const driveItem = {
      parentReference: {
        driveId: destinationGroupId,
        id: destinationFolderId,
      },
      "@microsoft.graph.conflictBehavior": "rename",
    }
    const resp = await sharepointApi.copyFile(null, sourceGroupId, fileId, driveItem)
    return resp.data
  }
}




//
// Common fetch wrapper (private) with retry logic for auth failures
//
async function callGraph(apiPath, no_base=true, extraHeaders={}, retryCount=0) {
  // Get the current token from store
  accessToken = store.state.aToken;

  // Check if token is expired before making the call
  if (tokenService.isTokenExpired(accessToken, 2)) {
    console.log('[Graph] Token expired or expiring soon, refreshing before call...');
    try {
      accessToken = await tokenService.refreshToken();
    } catch (refreshError) {
      console.error('[Graph] Pre-call token refresh failed:', refreshError);
      // Continue with existing token, let the request fail and trigger retry
    }
  }

  const url = `${no_base ? GRAPH_BASE: ''}${apiPath}`;

  try {
    let resp = await fetch(url, {
      headers: {
        authorization: `bearer ${accessToken}`,
        ...extraHeaders
      }
    });

    // Handle 401 Unauthorized - attempt token refresh and retry
    if (resp.status === 401 && retryCount < MAX_RETRIES) {
      console.log(`[Graph] 401 received, attempting token refresh (retry ${retryCount + 1}/${MAX_RETRIES})...`);

      try {
        const newToken = await tokenService.refreshToken();
        accessToken = newToken;

        // Wait a bit before retrying
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));

        // Retry the request with new token
        return callGraph(apiPath, no_base, extraHeaders, retryCount + 1);
      } catch (refreshError) {
        console.error('[Graph] Token refresh failed:', refreshError);

        // Dispatch auth error event
        window.dispatchEvent(new CustomEvent('auth-error', {
          detail: { error: refreshError, message: 'Session expired. Please log in again.' }
        }));

        throw new Error(`Authentication failed: ${refreshError.message}`);
      }
    }

    // Handle 403 Forbidden - access denied to the resource
    if (resp.status === 403) {
      console.error(`[Graph] 403 Forbidden for: ${apiPath}`);
      throw new GraphAccessDeniedError(apiPath);
    }

    if (!resp.ok) {
      const errorText = await resp.text().catch(() => resp.statusText);
      throw new Error(`Call to ${url} failed: ${resp.status} ${resp.statusText} - ${errorText}`);
    }

    return resp;
  } catch (error) {
    // Network errors or other fetch failures
    if (error.name === 'TypeError') {
      // When Graph redirects a /drive/ call to SharePoint and the user lacks
      // permission, SharePoint responds with AccessDenied.aspx which has no
      // CORS headers.  The browser surfaces this as a TypeError (network error)
      // rather than a readable 403.  Retrying won't help — it's a permissions
      // issue, not a transient network failure.
      const isDriveCall = apiPath.includes('/drive/') || apiPath.includes('/drive?');
      if (isDriveCall && retryCount > 0) {
        console.error(`[Graph] SharePoint access denied (CORS redirect) for: ${apiPath}`);
        throw new GraphAccessDeniedError(apiPath);
      }

      // For non-drive calls (or first attempt on drive calls), retry as it
      // may be a genuine transient network issue.
      if (retryCount < MAX_RETRIES) {
        console.log(`[Graph] Network error, retrying (${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return callGraph(apiPath, no_base, extraHeaders, retryCount + 1);
      }
    }
    throw error;
  }
}

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}